/**
 * useLibrary — **個人 library**（`decisions/018`；migration 0006 `deviceId`）
 *
 * 責任（app-local hook；硬規則 A：頁面唔准寫底層邏輯）：
 *   ① 一次 fetch 同一部機嘅**全部歌**（跨對話）→ 「我嘅歌」＋**播放清單**共用
 *   ② `running` 嘅歌自動輪詢 `/api/song/status/:id`（**嗰個請求本身就係完成動作**：
 *      查 provider → 下載音頻入 R2 → `succeeded`；唔輪詢首歌永遠唔會完成）
 *
 * ⚠️ 錯誤**照實**回（碼／原文），由頁面決定點講（唔准靜靜）
 */
import { useCallback, useEffect, useState } from 'react';
import { getDeviceId } from './useChatStream';

export interface LibrarySong {
  id: string;
  title: string;
  lyrics: string;
  moodTags: string[];
  status: string;
  error: string | null;
  createdAt?: string;
  audioKey: string | null;
  providerJobId?: string | null;
  variants?: { index: number; durationMs: number | null; hasHifi: boolean }[];
  pickedIndex?: number;
  /** ⭐ 0008（`decisions/027` 批次 B 日記）：用戶自己寫嘅感受（null ＝ 未寫） */
  journal?: string | null;
  /** ⭐ 0014（`decisions/034`）：風格 id（NULL ＝ 冇紀錄 → 介面唔顯示）＋ 來源 `auto`／`user` */
  genre?: string | null;
  genreSource?: string | null;
  /** ⭐ 0016（`decisions/039`）：用戶**自己改過歌名**嘅時間（null ＝ 仍然係 AI 寫嘅名） */
  titleEditedAt?: string | null;
}

export function useLibrary() {
  const [deviceId] = useState<string>(() => getDeviceId());
  const [songs, setSongs] = useState<LibrarySong[]>([]);
  const [hydrated, setHydrated] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      const res = await fetch(`/api/song/list?deviceId=${encodeURIComponent(deviceId)}`);
      const data = (await res.json().catch(() => ({}))) as { songs?: LibrarySong[]; error?: string };
      if (!res.ok) {
        setError(data.error ?? `http_${res.status}`);
        return false;
      }
      setSongs(data.songs ?? []);
      setError(null);
      return true;
    } catch (err) {
      setError(String(err).slice(0, 120));
      return false;
    }
  }, [deviceId]);

  useEffect(() => {
    let alive = true;
    (async () => {
      await reload();
      if (alive) setHydrated(true);
    })();
    return () => {
      alive = false;
    };
  }, [reload]);

  /** `running` → 5 秒一次輪詢（完成動作喺後端；失敗唔致命，下一輪再試） */
  useEffect(() => {
    const running = songs.filter((s) => s.status === 'running' && s.providerJobId);
    if (running.length === 0) return;
    let alive = true;
    const timer = setTimeout(async () => {
      const updates = await Promise.all(
        running.map(async (s) => {
          try {
            const res = await fetch(`/api/song/status/${s.id}`);
            if (!res.ok) return null;
            const data = (await res.json().catch(() => ({}))) as { song?: LibrarySong };
            return data.song ?? null;
          } catch {
            return null;
          }
        }),
      );
      if (!alive) return;
      setSongs((prev) => prev.map((s) => updates.find((u) => u && u.id === s.id) ?? s));
    }, 5000);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [songs]);

  return { deviceId, songs, setSongs, hydrated, error, reload };
}
